import { cn } from '@/lib/utils';
import type { Ticket } from '@/lib/api';

type TicketStatus = Ticket['status'];

/**
 * DESIGN.md §2 report palette badges — sharp 4px corners, mono-style label,
 * warm tonal fills so the list stays scannable without screaming color.
 */
const STYLES: Record<TicketStatus, { label: string; dot: string; fill: string; fg: string; border: string }> = {
  awaiting_approval: {
    label: 'Needs review',
    dot: '#ff5600',
    fill: '#fff0e6',
    fg: '#7a2a00',
    border: '#ffd3b3',
  },
  open: {
    label: 'Open',
    dot: '#65b5ff',
    fill: '#eaf4ff',
    fg: '#0a2b4a',
    border: '#bcdcff',
  },
  resolved: {
    label: 'Resolved',
    dot: '#0bdf50',
    fill: '#e6fbec',
    fg: '#0a3a16',
    border: '#b8edc6',
  },
  spam: {
    label: 'Spam',
    dot: '#7b7b78',
    fill: '#efece6',
    fg: '#313130',
    border: '#dedbd6',
  },
};

export function StatusBadge({
  status,
  size = 'sm',
  dotOnly,
  className,
}: {
  status: TicketStatus;
  size?: 'sm' | 'md';
  dotOnly?: boolean;
  className?: string;
}) {
  const s = STYLES[status];
  if (dotOnly) {
    return (
      <span
        aria-label={s.label}
        className={cn('inline-block rounded-full shrink-0', className)}
        style={{ width: 8, height: 8, background: s.dot }}
      />
    );
  }
  const pad = size === 'md' ? 'px-2.5 py-1 text-[11px]' : 'px-2 py-0.5 text-[10.5px]';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-sm border font-medium uppercase tracking-mono whitespace-nowrap',
        pad,
        className,
      )}
      style={{ background: s.fill, color: s.fg, borderColor: s.border }}
    >
      <span className="inline-block rounded-full" style={{ width: 6, height: 6, background: s.dot }} />
      {s.label}
    </span>
  );
}
